/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 * File Path: packages/sharedlib/src/utils/cloud-sync.util.ts
 *
 * General purpose cloud synchronization utilities
 */

import type { SyncItemData } from '@repo/common/types/AdapterSync';
import { get } from 'svelte/store';
import { getHandler } from '../sharedlib';
import { userAuthToken } from '../stores/auth.store';

/**
 * General purpose utility to upsert item data to the cloud
 * Moved from api.memory.ts to make it more clearly reusable across the app
 *
 * @param items - The sync item data containing arrays of items by table name
 */
export const upsertItemDataToCloud = async (items: SyncItemData) => {
  const $userAuthToken = get(userAuthToken);
  if (!$userAuthToken) {
    console.debug('[upsertItemDataToCloud] User not authenticated, skipping sync');
    return;
  }

  // Log the sync operation with reduced verbosity
  const itemCounts = Object.entries(items)
    .filter(([_, data]) => Array.isArray(data) && data.length > 0)
    .map(([table, data]) => `${table}: ${(data as any[]).length}`);

  if (itemCounts.length > 0) {
    console.debug('[upsertItemDataToCloud] Starting sync with items:', itemCounts.join(', '));
  }

  try {
    // Use the handler's syncItemData method to sync items with full data
    await getHandler().syncItemData(items, $userAuthToken);
    console.debug('[upsertItemDataToCloud] Sync completed successfully');
  } catch (error) {
    // Handle specific error types gracefully
    if (error instanceof Error) {
      const errorMessage = error.message.toLowerCase();

      // Network errors (404, 500, etc.) - log warning but don't crash
      if (
        errorMessage.includes('404') ||
        errorMessage.includes('500') ||
        errorMessage.includes('network') ||
        errorMessage.includes('timeout')
      ) {
        console.warn(
          '[upsertItemDataToCloud] Network error during sync, will retry later:',
          error.message
        );
        return;
      }

      // Authentication errors - log but don't crash
      if (
        errorMessage.includes('401') ||
        errorMessage.includes('403') ||
        errorMessage.includes('unauthorized') ||
        errorMessage.includes('forbidden')
      ) {
        console.warn('[upsertItemDataToCloud] Authentication error during sync:', error.message);
        return;
      }

      // Foreign key constraint errors - these are expected and will be retried
      if (errorMessage.includes('foreign key') || errorMessage.includes('constraint')) {
        console.debug(
          '[upsertItemDataToCloud] Foreign key constraint during sync, will retry later:',
          error.message
        );
        return;
      }
    }

    // For other errors, log but don't crash the app
    console.error('[upsertItemDataToCloud] Unexpected error during sync:', error);

    // Don't re-throw to prevent app destabilization
    // The sync will be retried on the next call
  }
};

/**
 * Utility to create a unique hash for workspace names to avoid duplicates
 * Uses a simple hash function suitable for workspace deduplication
 *
 * @param name - The workspace name to hash
 * @returns A hash string suitable for workspace deduplication
 */
export const createWorkspaceHash = (name: string): string => {
  // Simple hash function - can be improved if needed
  let hash = 0;
  if (name.length === 0) return hash.toString();

  for (let i = 0; i < name.length; i++) {
    const char = name.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  return Math.abs(hash).toString(36); // Convert to base36 for shorter strings
};
